import { Database, Pin } from 'lucide-react';
import type { ReactNode } from 'react';
import type { KnowledgeBaseWithIdentity } from '../../types/knowledge';
import { CrawlJobsGroup } from './CrawlJobsGroup';
import { KnowledgeDatabaseCard } from './KnowledgeDatabaseCard';

type KnowledgePageDatabaseListProps = {
  isPageLoading: boolean;
  databasesError: boolean;
  databases: KnowledgeBaseWithIdentity[];
  filteredDatabases: KnowledgeBaseWithIdentity[];
  pinnedDatabases: KnowledgeBaseWithIdentity[];
  unpinnedDatabases: KnowledgeBaseWithIdentity[];
  isPinnedGroupExpanded: boolean;
  translate: (key: string, params?: Record<string, string>) => string;
  formatLastModified: (value: string | null | undefined) => string;
  onTogglePinnedGroup: () => void;
  onTogglePin: (databaseId: string) => void;
  onOpenDatabase: (databaseId: string) => void;
};

export function KnowledgePageDatabaseList({
  isPageLoading,
  databasesError,
  databases,
  filteredDatabases,
  pinnedDatabases,
  unpinnedDatabases,
  isPinnedGroupExpanded,
  translate,
  formatLastModified,
  onTogglePinnedGroup,
  onTogglePin,
  onOpenDatabase,
}: KnowledgePageDatabaseListProps): ReactNode {
  if (isPageLoading) {
    return (
      <p className="knowledge-page__status">{translate('knowledge.loading')}</p>
    );
  }

  if (databasesError) {
    return (
      <p className="knowledge-page__status" role="alert">
        {translate('knowledge.loadError')}
      </p>
    );
  }

  if (databases.length === 0) {
    return (
      <div className="knowledge-page__empty">
        <Database
          className="knowledge-page__empty-icon"
          strokeWidth={1.75}
          aria-hidden
        />
        <p>{translate('knowledge.empty')}</p>
      </div>
    );
  }

  if (filteredDatabases.length === 0) {
    return (
      <div className="knowledge-page__empty">
        <Database
          className="knowledge-page__empty-icon"
          strokeWidth={1.75}
          aria-hidden
        />
        <p>{translate('knowledge.databasesNoResults')}</p>
      </div>
    );
  }

  return (
    <div className="knowledge-page__sections">
      {pinnedDatabases.length > 0 && (
        <CrawlJobsGroup
          id="pinned-databases"
          className="knowledge-page__pinned-group"
          title={translate('knowledge.pinnedDatabases')}
          count={pinnedDatabases.length}
          icon={Pin}
          iconFilled
          isExpanded={isPinnedGroupExpanded}
          onToggle={onTogglePinnedGroup}
          expandLabel={translate('knowledge.pinnedDatabasesExpand')}
          collapseLabel={translate('knowledge.pinnedDatabasesCollapse')}
          emptyMessage={translate('knowledge.pinnedDatabasesEmpty')}
        >
          <ul className="knowledge-page__grid">
            {pinnedDatabases.map((database) => (
              <KnowledgeDatabaseCard
                key={database.id}
                database={database}
                isPinned
                lastModifiedLabel={formatLastModified(database.lastModified)}
                translate={translate}
                onTogglePin={onTogglePin}
                onOpen={onOpenDatabase}
              />
            ))}
          </ul>
        </CrawlJobsGroup>
      )}
      {unpinnedDatabases.length > 0 && (
        <ul className="knowledge-page__grid">
          {unpinnedDatabases.map((database) => (
            <KnowledgeDatabaseCard
              key={database.id}
              database={database}
              isPinned={false}
              lastModifiedLabel={formatLastModified(database.lastModified)}
              translate={translate}
              onTogglePin={onTogglePin}
              onOpen={onOpenDatabase}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
